import { zodResolver } from '@hookform/resolvers/zod';
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DiscountAndFee, useProducts } from '../../../data-access';
import {
  DiscountsNameSortOrder,
  DiscountsQueryInputs,
  DiscountsQuerySchema,
} from '../components/DiscountsQueryForm';

interface DiscountsQueryContextValue {
  searchTerm: string;
  nameSortOrder: DiscountsNameSortOrder;
  discountsFiltered: DiscountAndFee[];
}

const DiscountsQueryContext = createContext<
  DiscountsQueryContextValue | undefined
>(undefined);

export const DiscountsQueryProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { availableDiscounts } = useProducts();

  const discountsQueryForm = useForm<DiscountsQueryInputs>({
    defaultValues: { searchTerm: '', nameSortOrder: 'asc' },
    resolver: zodResolver(DiscountsQuerySchema),
  });

  const searchTerm = discountsQueryForm.watch('searchTerm');
  const nameSortOrder = discountsQueryForm.watch('nameSortOrder');

  const discountsFiltered = useMemo(
    () =>
      availableDiscounts
        .filter((d) =>
          [d.description, d.displayName]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => {
          const cmp = a.displayName.localeCompare(b.displayName);
          return nameSortOrder === 'asc' ? cmp : -cmp;
        }),
    [availableDiscounts, searchTerm, nameSortOrder]
  );

  return (
    <DiscountsQueryContext.Provider
      value={{ searchTerm, nameSortOrder, discountsFiltered }}
    >
      <FormProvider {...discountsQueryForm}>{children}</FormProvider>
    </DiscountsQueryContext.Provider>
  );
};

export const useDiscountsQuery = () => {
  const context = useContext(DiscountsQueryContext);
  if (context === undefined) {
    throw new Error(
      'useDiscountsQuery must be used within a DiscountsQueryProvider'
    );
  }
  return context;
};
